// apps/portal/src/hooks/useSchedules.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenantContext } from 'src/context/useTenantContext';
import {
  scheduleAssign,
  scheduleCopy,
  scheduleCreate,
  scheduleDelete,
  scheduleList,
  scheduleMine,
  schedulePublish,
  scheduleRegister,
  scheduleUnassign,
  scheduleUpdate,
  scheduleWithdraw,
  timesheetEntryScheduleLink,
} from 'src/services/apis/tenantRequests';
import {
  CopyResultDto,
  CopyWeekDto,
  CreateShiftDto,
  MyScheduleDto,
  PublishResultDto,
  PublishWeekDto,
  ScheduleListDto,
  UpdateShiftDto,
} from 'src/services/apis/types';
import { QUERIES, unwrapped } from 'src/utils/react-query';

/**
 * Every schedule write refreshes the schedules lists, the caller's own schedule and the
 * timesheet calendar (its `planned[]` and worked-shift labels come from the same tables).
 * onSettled, not onSuccess: a 409 means the server moved under us — refetch either way.
 */
function useRefreshSchedules() {
  const { orgSlug, restaurantId } = useTenantContext();
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries([QUERIES.SCHEDULES, orgSlug, restaurantId]),
      queryClient.invalidateQueries([QUERIES.SCHEDULE_ME, orgSlug, restaurantId]),
      queryClient.invalidateQueries([QUERIES.TIMESHEET_CALENDAR, orgSlug, restaurantId]),
      // Prefix key (no branchId/id): the detail page's linked-shift label must not go stale.
      queryClient.invalidateQueries([QUERIES.TIMESHEET_DETAIL, orgSlug, restaurantId]),
    ]);
}

/** Manager list (≤ 42 days): shifts with assignees and flags, plus the eligible staff. */
export const useSchedules = (from: string, to: string, enabled = true) => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  return useQuery(
    [QUERIES.SCHEDULES, orgSlug, restaurantId, branchId, from, to],
    () => unwrapped<ScheduleListDto>(scheduleList({ orgSlug, restaurantId, branchId }, from, to)),
    // The app default is refetchOnMount: false; a dialog must see who is busy now.
    { enabled: enabled && Boolean(from && to), refetchOnMount: true }
  );
};

export const useScheduleActions = () => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const scope = { orgSlug, restaurantId, branchId };
  const refresh = useRefreshSchedules();
  const opts = { onSettled: refresh };
  return {
    create: useMutation((body: CreateShiftDto) => scheduleCreate(scope, body), opts),
    update: useMutation(({ id, body }: { id: string; body: UpdateShiftDto }) => scheduleUpdate(scope, id, body), opts),
    remove: useMutation((id: string) => scheduleDelete(scope, id), opts),
    assign: useMutation(
      ({ id, employeeId }: { id: string; employeeId: string }) => scheduleAssign(scope, id, employeeId),
      opts
    ),
    unassign: useMutation(
      ({ id, employeeId }: { id: string; employeeId: string }) => scheduleUnassign(scope, id, employeeId),
      opts
    ),
    publish: useMutation((body: PublishWeekDto) => unwrapped<PublishResultDto>(schedulePublish(scope, body)), opts),
    copy: useMutation((body: CopyWeekDto) => unwrapped<CopyResultDto>(scheduleCopy(scope, body)), opts),
  };
};

/** The caller's own seats and the open shifts they could take (≤ 42 days). */
export const useMySchedule = (from: string, to: string, enabled = true) => {
  const { orgSlug, restaurantId } = useTenantContext();
  return useQuery(
    [QUERIES.SCHEDULE_ME, orgSlug, restaurantId, from, to],
    () => unwrapped<MyScheduleDto>(scheduleMine({ orgSlug, restaurantId }, from, to)),
    { enabled: enabled && Boolean(from && to), refetchOnMount: true }
  );
};

export const useMyScheduleActions = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  const scope = { orgSlug, restaurantId };
  const refresh = useRefreshSchedules();
  const opts = { onSettled: refresh };
  return {
    register: useMutation((shiftId: string) => scheduleRegister(scope, shiftId), opts),
    withdraw: useMutation((shiftId: string) => scheduleWithdraw(scope, shiftId), opts),
  };
};

/** Manual link/unlink of a time entry to a planned seat (spec §4.1). */
export const useLinkEntrySchedule = (timesheetId: string) => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const queryClient = useQueryClient();
  const refresh = useRefreshSchedules();
  return useMutation(
    ({ entryId, assignmentId }: { entryId: string; assignmentId: string | null }) =>
      timesheetEntryScheduleLink({ orgSlug, restaurantId, branchId }, entryId, assignmentId),
    {
      onSettled: () =>
        Promise.all([
          queryClient.invalidateQueries([QUERIES.TIMESHEET_DETAIL, orgSlug, restaurantId, branchId, timesheetId]),
          refresh(),
        ]),
    }
  );
};
